import zillowImg from '../assets/images/zillow.svg'
import mtcImg from '../assets/images/metatypingclub.svg'
import amazonImg from '../assets/images/amazon-clone.svg'
import wfImg from '../assets/images/wf-clone.svg'
import fbImg from '../assets/images/facebook-clone.svg'
import netflixImg from '../assets/images/netflix-clone.svg'
import youtubeImg from '../assets/images/youtube-clone.svg'
import instagramImg from '../assets/images/instagram-clone.svg'
import { t } from 'framer-motion/dist/types.d-CtuPurYT'

export interface Project {
  id: string
  title: string
  shortDescription: string
  fullDescription: string
  technologies: string[]
  link?: string
  github?: string
  featured?: boolean
  status: 'Featured' | 'In Development' | 'Coming Soon'
  image: string
  videoUrl?: string // YouTube video URL
  requiresAccess?: boolean // Whether the source code requires access request
}

export const projects: Project[] = [
  {
    id: 'metatypingclub',
    title: 'MetaTypingClub',
    shortDescription: `A groundbreaking multilingual typing tutor platform that has gained 5,000+ users, particularly serving underrepresented languages like Pashto, Dari, and Persian. The platform was born from a need to provide typing education for languages that lacked dedicated typing tutors.`,
    fullDescription: `Key Technical Achievements:
• Implemented a sophisticated language system using i18next, supporting both LTR (English, Russian) and RTL (Persian, Pashto, Dari) languages, with independent typing language selection
• Developed a centralized styling system using styled-components with theme support (light/dark modes)
• Created a robust state management system using mobx-state-tree for complex user interactions and typing statistics
• Built custom authentication system with features like password recovery, email verification, and secure session management
• Integrated PayPal for donations and Google Sheets API for multilingual feedback collection
• Implemented sound effects using use-sound with user controls
• Added smooth animations and transitions using Framer Motion
• Utilized EmailJS for automated email communications
• Implemented comprehensive error handling and user feedback systems
• Created a responsive dashboard with real-time statistics and progress tracking
• Used localStorage and sessionStorage for efficient client-side data management
• Developed reusable components for consistent UI across the application

The platform's success lies in its unique combination of technical innovation and user-centric design, making typing education accessible to previously underserved language communities.`,
    technologies: [
      'React',
      'GraphQL',
      'Node.js',
      'MongoDB',
      'JavaScript',
      'Apollo Client',
      'MobX-State-Tree',
      'Styled-Components',
      'i18next',
      'Framer Motion',
      'use-sound',
      'EmailJS',
      'PayPal API',
      'Google Sheets API',
      'JWT Authentication',
      'RTL Support',
      'Theme System',
      'Error Handling',
      'LocalStorage',
      'SessionStorage',
      'React Icons',
    ],
    link: 'https://metatypingclub.com',
    github: 'https://github.com/yourusername/metatypingclub',
    featured: true,
    status: 'Featured',
    image: mtcImg,
    videoUrl: 'https://youtu.be/3nCLj3cGEsA',
    requiresAccess: true,
  },
  {
    id: 'zillow',
    title: 'Zillow Clone',
    shortDescription: `A modern real estate platform with advanced search, property listings, and virtual tours.`,
    fullDescription: `Key Features:
• Interactive property maps with detailed location information
• Virtual tours using 3D technology for immersive property viewing
• Advanced search functionality with multiple filters
• Detailed property information and analytics
• User-friendly interface for both buyers and sellers
• Real-time property updates and notifications
• Secure user authentication and profile management
• Responsive design for all devices

The platform combines cutting-edge technology with intuitive design to revolutionize the property search experience.`,
    technologies: [
      'Next.js',
      'TypeScript',
      'GraphQL',
      'AWS',
      '3D Tours',
      'Interactive Maps',
      'User Authentication',
      'Property Management',
      'Search Optimization',
      'Responsive Design',
    ],
    status: 'Featured',
    featured: true,
    github: 'https://github.com/dzirmal/Real-estate',
    link: '#',
    image: zillowImg,
    requiresAccess: true,
  },
  {
    id: 'amazon-clone',
    title: 'Amazon Clone',
    shortDescription: `Full-featured e-commerce platform with product search, cart management, and secure payments.`,
    fullDescription: `Key Features:
• Comprehensive product search with advanced filtering
• Secure shopping cart and wishlist management
• User authentication with multiple security layers
• Detailed product reviews and ratings system
• Real-time order tracking and management
• Seller dashboard with analytics and inventory management
• Secure payment processing with multiple options
• Responsive design for seamless shopping experience

The platform is built with scalability in mind, providing a robust foundation for both buyers and sellers.`,
    technologies: [
      'React',
      'Node.js',
      'MongoDB',
      'Stripe',
      'Redux',
      'User Authentication',
      'Payment Processing',
      'Order Management',
      'Product Search',
      'Seller Dashboard',
    ],
    status: 'In Development',
    featured: true,
    github: 'https://github.com/dzirmal/amz-e-commerce-full-clone',
    link: '#',
    image: amazonImg,
    requiresAccess: false,
  },
  {
    id: 'wf-clone',
    title: 'WF Clone',
    shortDescription: `Secure banking platform with account management, transactions, and financial tools.`,
    fullDescription: `Key Features:
• Multi-layer security authentication system
• Comprehensive transaction history and management
• Automated bill payments and scheduling
• Real-time financial analytics and reporting
• Secure fund transfers and payments
• Account management and customization
• Mobile-responsive banking interface
• Advanced fraud detection and prevention

The platform prioritizes security while delivering a seamless banking experience.`,
    technologies: [
      'React',
      'TypeScript',
      'GraphQL',
      'Security',
      'AWS',
      'User Authentication',
      'Transaction Processing',
      'Financial Analytics',
      'Bill Payments',
      'Secure API',
    ],
    status: 'Coming Soon',
    featured: true,
    link: '#',
    image: wfImg,
  },
  {
    id: 'facebook-clone',
    title: 'FB Clone',
    shortDescription: `Modern social network with real-time updates, friend connections, and content sharing.`,
    fullDescription: `Key Features:
• Real-time messaging and notifications
• Dynamic content creation and sharing
• Comprehensive user profiles and privacy settings
• Interactive news feed with smart algorithms
• Friend connection and social networking
• Media sharing and content management
• Community building and group features
• Mobile-first responsive design

The platform emphasizes user engagement while maintaining privacy and security.`,
    technologies: [
      'React',
      'GraphQL',
      'WebSocket',
      'AWS',
      'Redis',
      'Real-time Updates',
      'User Authentication',
      'Content Management',
      'Messaging System',
      'News Feed',
    ],
    status: 'In Development',
    featured: true,
    github: 'https://github.com/dzirmal/Facebook-clone',
    link: '#',
    image: fbImg,
    requiresAccess: true,
  },
  {
    id: 'netflix-clone',
    title: 'Netflix Clone',
    shortDescription: `Streaming platform with video playback, user profiles, and content recommendations.`,
    fullDescription: `Key Features:
• High-quality video streaming with adaptive bitrate
• Personalized user profiles and preferences
• Smart content recommendation system
• Dynamic content categories and browsing
• Seamless playback across devices
• User ratings and reviews
• Content discovery and search
• Responsive design for all screen sizes

The platform focuses on delivering an exceptional streaming experience with intuitive navigation.`,
    technologies: [
      'React',
      'Node.js',
      'MongoDB',
      'AWS',
      'Video Streaming',
      'User Profiles',
      'Content Management',
      'Recommendation System',
      'Video Processing',
      'Responsive Design',
    ],
    status: 'In Development',
    featured: true,
    github: 'https://github.com/dzirmal/netflix-clone',
    link: '#',
    image: netflixImg,
  },
  {
    id: 'youtube-clone',
    title: 'YouTube Clone',
    shortDescription: `Video sharing platform with upload, playback, and content management features.`,
    fullDescription: `Key Features:
• High-quality video upload and processing
• Advanced video player with playback controls
• Interactive comment and discussion system
• Channel management and customization
• Content discovery and recommendations
• User authentication and profile management
• Video analytics and insights
• Responsive design for all devices

The platform provides a comprehensive solution for content creators and viewers alike.`,
    technologies: [
      'React',
      'Node.js',
      'MongoDB',
      'AWS',
      'Video Processing',
      'User Authentication',
      'Content Management',
      'Comment System',
      'Channel Management',
      'Video Analytics',
    ],
    status: 'In Development',
    featured: true,
    github: 'https://github.com/dzirmal/YouTube-clone',
    link: '#',
    image: youtubeImg,
  },
  {
    id: 'instagram-clone',
    title: 'Instagram Clone',
    shortDescription: `A modern photo-sharing social network inspired by Instagram.`,
    fullDescription: `Key Features:
• Photo upload with cropping and filters
• Real-time feed and stories
• Like, comment, and follow system
• User profiles with bio and photo grid
• Explore page with trending posts
• Direct messaging between users
• Push notifications for engagement
• Responsive mobile-first design

The platform is designed for seamless sharing and discovery of visual content, focusing on user engagement and community building.`,
    technologies: [
      'React',
      'Node.js',
      'MongoDB',
      'AWS',
      'GraphQL',
      'Redux',
      'Socket.io',
      'Image Processing',
      'User Authentication',
      'Responsive Design',
    ],
    status: 'In Development',
    featured: true,
    github: 'https://github.com/dzirmal/Instagram-clone',
    link: '#',
    image: instagramImg,
    requiresAccess: true,
  },
]
